import { StarIcon } from 'assets/Icons';
import React from 'react';
import { SubscriptionPackage } from 'TYPES/api.body';

interface Props {
  subscription: SubscriptionPackage;
  onSelect: (sub: SubscriptionPackage) => void;
  isPopular?: boolean;
}

// Enhanced benefits data based on subscription type
const getEnhancedBenefits = (name: string) => {
  const commonBenefits = [
    'Priority customer support',
    'Exclusive member discounts',
    'Early access to new products',
    'Free delivery on all orders'
  ];

  const specificBenefits = {
    '1 Day': [
      'Perfect for trying out premium features',
      '24-hour access to all premium content',
      'No commitment required',
      ...commonBenefits.slice(0, 2)
    ],
    Weekly: [
      // 'Great for short-term premium access',
      // '2 weeks of uninterrupted premium service',
      'Weekly exclusive deals and offers',
      ...commonBenefits
    ],
    Monthly: [
      'Most popular choice among users',
      'Full month of premium features',
      'Monthly subscriber-only events',
      'Advanced analytics and insights',
      ...commonBenefits
    ],
    Yearly: [
      'Best value for money - Save up to 60%',
      'Full year of premium access',
      'Quarterly premium gift boxes',
      'VIP customer status',
      // 'Annual exclusive member conference access',
      // 'Personal account manager',
      ...commonBenefits
    ]
  };

  if (name in specificBenefits) {
    return specificBenefits[name as keyof typeof specificBenefits];
  }
  return commonBenefits;
};

// Calculate savings percentage
const getSavingsInfo = (name: string, amount: number) => {
  const baseMonthlyPrice = 500;
  const annualDiscount = {
    '1 Day': { daily: amount, monthly: baseMonthlyPrice },
    Weekly: { biweekly: amount, monthly: baseMonthlyPrice },
    Monthly: { monthly: amount },
    Yearly: { yearly: amount, monthly: baseMonthlyPrice }
  };

  if (name === 'Yearly') {
    const monthlyEquivalent = amount / 12;
    const savings = (((baseMonthlyPrice - monthlyEquivalent) / baseMonthlyPrice) * 100).toFixed(0);
    return { savings: `${savings}%`, monthlyEquivalent: monthlyEquivalent.toFixed(2) };
  }

  return null;
};

const SubscriptionCard: React.FC<Props> = ({ subscription, onSelect, isPopular = false }) => {
  const enhancedBenefits = getEnhancedBenefits(subscription.name);
  const savingsInfo = getSavingsInfo(subscription.name, subscription.amount);

  return (
    <div className="w-full max-w-[400px] mx-auto">
      {/* Main Card */}
      <div
        className={`relative transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${
          isPopular ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
        }`}>
        {/* Popular Badge */}
        {isPopular && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
              <StarIcon className="w-3 h-3 fill-current" />
              Most Popular
            </div>
          </div>
        )}

        <div
          className={`${
            isPopular ? 'bg-gradient-to-br from-blue-600 to-purple-700' : 'bg-primary'
          } w-full flex flex-col gap-4 p-4 sm:p-6 rounded-xl shadow-lg`}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-white">
              {/* <TbShoppingBagDiscount className="w-5 h-5" fill="white" /> */}
              <p className="font-medium">Madina Plus</p>
            </div>
            {subscription.legibleForFreeTrial && (
              <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">Free Trial</div>
            )}
          </div>

          {/* Duration Badge */}
          <div className="flex items-center gap-2 text-white/90">
            {/* <BsClock className="w-4 h-4" /> */}
            <span className="text-sm">
              {subscription.duration} day{subscription.duration > 1 ? 's' : ''} access
            </span>
          </div>

          {/* Pricing */}
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl sm:text-4xl font-bold text-white">₦{subscription.amount.toLocaleString()}</span>
              <span className="text-white/70 text-sm">/{subscription.name.toLowerCase()}</span>
            </div>

            {savingsInfo && (
              <div className="space-y-1">
                <div className="text-green-200 text-sm font-medium">Save {savingsInfo.savings} vs monthly</div>
                <div className="text-white/70 text-xs">Just ₦{savingsInfo.monthlyEquivalent}/month</div>
              </div>
            )}
          </div>

          {/* Subscribe Button */}
          <button
            type="button"
            onClick={() => onSelect(subscription)}
            className="w-full sm:w-auto hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 
                     p-3 px-6 rounded-lg text-primary text-sm font-semibold bg-white shadow-md
                     transform hover:translate-y-[-1px] active:translate-y-0">
            Get {subscription.name} Plan
          </button>
        </div>
      </div>

      {/* Details Section */}
      <div className="mt-8 sm:mt-12 lg:mt-14">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Madina Plus {subscription?.name} Benefits</h2>
          {subscription.name === 'Yearly'}
        </div>

        <hr className="border-gray-200 mb-6" />

        <div className="space-y-4">
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
            Unlock premium savings and exclusive benefits with our {subscription?.name.toLowerCase()} subscription plan.
            {subscription.name === 'Yearly' && ' Get the best value with our annual plan!'}
            {subscription.name === 'Monthly' && ' Perfect for regular shoppers who want consistent savings.'}
            {subscription.name === 'Weekly' && ' Ideal for trying our premium features over a longer period.'}
            {subscription.name === '1 Day' && ' Perfect for special occasions or trying our premium service.'}
          </p>

          {/* Benefits List */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">What's included:</h3>
            <ul className="space-y-2">
              {enhancedBenefits.map((benefit: string, index: number) => (
                <li key={index} className="flex items-start gap-3 text-gray-700 text-sm sm:text-base">
                  {/* <BsCheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mt-0.5 flex-shrink-0" /> */}
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Additional Info for Yearly Plan */}

          {/* Call to Action */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-3">
              {subscription.legibleForFreeTrial
                ? 'Start your free trial today and experience the difference!'
                : 'Ready to upgrade your shopping experience?'}
            </p>
            <button
              onClick={() => onSelect(subscription)}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg 
                       font-semibold transition-colors duration-200 text-sm">
              Choose {subscription.name} Plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;
